import { existsSync, readFileSync, readdirSync } from 'node:fs';
import { join } from 'node:path';
import matter from 'gray-matter';
import { computeFreshness } from '../lib/freshness.js';
import { log } from '../lib/log.js';
import { summarizePendingSessions } from '../lib/session-start.js';
import { readAllNodes } from '../lib/nodes.js';
import { findRepoRoot, repoPaths } from '../lib/paths.js';

export async function runStatus(): Promise<number> {
  const root = findRepoRoot();
  const paths = repoPaths(root);

  if (!existsSync(paths.installedVersionFile)) {
    log.warn(
      'kenkeep is not initialized in this repo. Run `npx kenkeep init --harnesses <id[,id,...]>`.'
    );
    return 1;
  }

  const installed = JSON.parse(readFileSync(paths.installedVersionFile, 'utf8')) as {
    version: string;
    installed_at: string;
  };

  const sessionStats = scanSessions(paths.sessionsDir);
  const curationQueue = summarizePendingSessions(paths.sessionsDir);
  const nodeCounts = countNodes(paths.nodesDir);
  const freshness = computeFreshness({ root, nodesDir: paths.nodesDir });

  log.plain(`kenkeep v${installed.version} (installed ${installed.installed_at})`);
  log.plain('');
  log.plain('Knowledge base');
  log.plain(`  Practice nodes: ${nodeCounts.practice}`);
  log.plain(`  Map nodes:      ${nodeCounts.map}`);
  log.plain(
    `  Nodes describing changed code: ${freshness.available ? freshness.flaggedCount : 'n/a'}`
  );
  log.plain('');
  log.plain('Pending work');
  log.plain(`  Proposal extraction queue: ${sessionStats.awaitingExtraction}`);
  log.plain(`  Curation queue:           ${curationQueue.pending}`);
  log.plain(`  Candidate proposals:      ${curationQueue.candidateCount}`);
  log.plain('');
  log.plain('Session log proposal status');
  log.plain(`  Awaiting extraction:      ${sessionStats.awaitingExtraction}`);
  log.plain(`  Extracted:                ${sessionStats.extracted}`);
  log.plain(`  Failed extraction:        ${sessionStats.failed}`);
  log.plain(`  Skipped extraction:       ${sessionStats.skipped}`);
  return 0;
}

function scanSessions(dir: string): {
  awaitingExtraction: number;
  extracted: number;
  failed: number;
  skipped: number;
} {
  const out = { awaitingExtraction: 0, extracted: 0, failed: 0, skipped: 0 };
  if (!existsSync(dir)) return out;
  for (const name of readdirSync(dir)) {
    if (!name.endsWith('.md')) continue;
    const file = join(dir, name);
    try {
      const fm = matter(readFileSync(file, 'utf8')).data as { proposal_status?: string };
      switch (fm.proposal_status) {
        case 'done':
          out.extracted += 1;
          break;
        case 'failed':
          out.failed += 1;
          break;
        case 'skipped':
          out.skipped += 1;
          break;
        default:
          out.awaitingExtraction += 1;
      }
    } catch {
      out.awaitingExtraction += 1;
    }
  }
  return out;
}

/**
 * Counts nodes by their `kind` frontmatter facet across the whole topical tree.
 * `kind` is a facet, not a directory: leaves live in topical folders at any
 * depth, so we read every leaf and bucket by frontmatter, not by a
 * `nodes/practice` / `nodes/map` directory (which no longer exist). On a
 * malformed or old-layout tree `readAllNodes` throws; `status` is a quick
 * summary, so we degrade to zeros and let `doctor` surface the details.
 */
function countNodes(nodesDir: string): { practice: number; map: number } {
  const out = { practice: 0, map: 0 };
  if (!existsSync(nodesDir)) return out;
  try {
    for (const node of readAllNodes(nodesDir)) {
      if (node.frontmatter.type === 'practice') out.practice += 1;
      else if (node.frontmatter.type === 'map') out.map += 1;
    }
  } catch {
    return out;
  }
  return out;
}
